import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { deckSubmissionService } from '../../services/deckSubmissionService';
import { storageService } from '../../services/storageService';
import { Helmet } from 'react-helmet';
import RoleBasedHeader from '../../components/ui/RoleBasedHeader';
import AnnouncementBanner from '../../components/ui/AnnouncementBanner';
import Button from '../../components/ui/Button';
import UnifiedUploadSection from './components/UnifiedUploadSection';
import FilePreviewCard from './components/FilePreviewCard';
import PresentationContextForm from './components/PresentationContextForm';
import SubmissionSuccessModal from './components/SubmissionSuccessModal';
import AuditTypeSelector from './components/AuditTypeSelector';
import Icon from '../../components/AppIcon';
import { emailService } from '../../services/emailService';

// ── PDF page counter ──────────────────────────────────────────────────────────
const getPdfPageCount = (file) => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const typedArray = new Uint8Array(e.target.result);
        const text = new TextDecoder('latin1').decode(typedArray);
        const pageMatches = text.match(/\/Type\s*\/Page[^s]/g);
        if (pageMatches && pageMatches.length > 0) { resolve(pageMatches.length); return; }
        const countMatch = text.match(/\/Count\s+(\d+)/);
        if (countMatch) { resolve(parseInt(countMatch[1], 10)); return; }
        resolve(1);
      } catch { resolve(1); }
    };
    reader.onerror = () => resolve(1);
    reader.readAsArrayBuffer(file);
  });
};

const getPptxSlideCount = (fileSize) => Math.max(1, Math.round(fileSize / 150000));

// ── Responsive hook ───────────────────────────────────────────────────────────
const useIsMobile = () => {
  const [isMobile, setIsMobile] = React.useState(() => window.innerWidth < 768);
  React.useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isMobile;
};

// ── App-wide gradient constant ────────────────────────────────────────────────
// Used inline where needed; also injected as --app-gradient CSS variable
// so AuditTypeSelector and other child components can reference it.
const APP_GRADIENT = 'linear-gradient(135deg, #090A0B 0%, #001a4d 40%, #004AAD 75%, #0082FB 100%)';

// ── Page-level styles ─────────────────────────────────────────────────────────
const PAGE_CSS = `
  @font-face {
    font-family: 'Sansation';
    src: local('Sansation Regular'), local('Sansation'), local('Sansation-Regular');
    font-weight: 400;
    font-style: normal;
  }
  @font-face {
    font-family: 'Sansation';
    src: local('Sansation Bold'), local('Sansation-Bold');
    font-weight: 700;
    font-style: normal;
  }

  /* ── CSS variable: makes gradient available to all child components ── */
  .ifs-root {
    --app-gradient: linear-gradient(135deg, #090A0B 0%, #001a4d 40%, #004AAD 75%, #0082FB 100%);
    --app-blue: #004AAD;
    --app-blue-soft: rgba(0, 74, 173, 0.07);
    --app-blue-shadow: rgba(0, 74, 173, 0.32);
  }

  /* ── Any direct gradient button inside this page ── */
  .ifs-root .btn-primary,
  .ifs-root button[data-variant="primary"],
  .ifs-root .audit-submit-btn {
    background: var(--app-gradient) !important;
    box-shadow: 0 4px 16px var(--app-blue-shadow) !important;
  }
  .ifs-root .btn-primary:hover:not(:disabled),
  .ifs-root button[data-variant="primary"]:hover:not(:disabled),
  .ifs-root .audit-submit-btn:hover:not(:disabled) {
    opacity: 0.92;
    transform: translateY(-1px);
  }

  /* ── Headings use Sansation ── */
  .ifs-root h1,
  .ifs-root h2,
  .ifs-root h3 {
    font-family: 'Sansation', sans-serif;
  }
`;

// ─────────────────────────────────────────────────────────────────────────────
const InitialFormUploadScreen = () => {
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();
  const isMobile = useIsMobile();

  const [uploadedFiles, setUploadedFiles]         = useState([]);
  const [isSubmitting, setIsSubmitting]           = useState(false);
  const [showSuccessModal, setShowSuccessModal]   = useState(false);
  const [auditType, setAuditType]                 = useState('free');
  const [formData, setFormData] = useState({
    title: '', industry: '', audience: '', purpose: '', notes: '',
  });
  const [errors, setErrors]               = useState({});
  const [touched, setTouched]             = useState({});
  const [successMessage, setSuccessMessage]     = useState('');
  const [validationAlert, setValidationAlert]   = useState('');
  const [currency, setCurrency] = useState('USD');

  const maxFileSize     = 50;
  const acceptedFormats = '.pptx,.ppt,.pdf';

  // ── Validation ──────────────────────────────────────────────────────────────
  const validateField = (field, value) => {
    switch (field) {
      case 'title':
        if (!value?.trim()) return 'Presentation title is required';
        if (value?.trim()?.length < 3) return 'Title must be at least 3 characters';
        return '';
      case 'industry': return !value ? 'Please select an industry' : '';
      case 'audience': return !value ? 'Please select target audience' : '';
      case 'purpose':  return !value ? 'Please select presentation purpose' : '';
      default: return '';
    }
  };

  const handleFieldBlur = (field) => {
    setTouched({ ...touched, [field]: true });
    setErrors({ ...errors, [field]: validateField(field, formData?.[field]) });
  };

  const handleFormDataChange = (newFormData) => {
    setFormData(newFormData);
    if (successMessage) setSuccessMessage('');
    if (validationAlert) setValidationAlert('');
    const updatedErrors = { ...errors };
    Object.keys(touched)?.forEach(field => {
      if (touched?.[field]) updatedErrors[field] = validateField(field, newFormData?.[field]);
    });
    setErrors(updatedErrors);
  };

  const validateFile = (file) => {
    const maxSizeBytes      = maxFileSize * 1024 * 1024;
    const allowedExtensions = ['pptx', 'ppt', 'pdf'];
    const fileExtension     = file?.name?.split('.')?.pop()?.toLowerCase();
    if (file?.size > maxSizeBytes) return `File size exceeds ${maxFileSize}MB limit`;
    if (!allowedExtensions?.includes(fileExtension))
      return 'Invalid file format. Please upload .pptx, .ppt, or .pdf files';
    return null;
  };

  // ── File handlers ───────────────────────────────────────────────────────────
  const handleFileSelect = (file) => {
    const validationError = validateFile(file);
    if (validationError) { setErrors({ ...errors, file: validationError }); return; }
    const newFile = {
      id: Date.now(), name: file?.name, size: file?.size,
      type: 'file', uploadProgress: 0, fileObject: file,
    };
    setUploadedFiles([...uploadedFiles, newFile]);
    setErrors({ ...errors, file: '' });
    setSuccessMessage('');
    if (validationAlert) setValidationAlert('');
    simulateUpload(newFile?.id);
  };

  const handleLinkSubmit = (url) => {
    const newFile = {
      id: Date.now(), name: 'Google Slides Presentation',
      size: 0, type: 'link', url, uploadProgress: 100,
    };
    setUploadedFiles([...uploadedFiles, newFile]);
    setErrors({ ...errors, file: '' });
    setSuccessMessage('');
    if (validationAlert) setValidationAlert('');
  };

  const simulateUpload = (fileId) => {
    let progress = 0;
    const interval = setInterval(() => {
      progress += 10;
      setUploadedFiles((prev) =>
        prev?.map((file) => file?.id === fileId ? { ...file, uploadProgress: progress } : file)
      );
      if (progress >= 100) clearInterval(interval);
    }, 300);
  };

  const handleRemoveFile = (fileId) => {
    setUploadedFiles(uploadedFiles?.filter((file) => file?.id !== fileId));
    setSuccessMessage('');
  };

  // ── Form validation ─────────────────────────────────────────────────────────
  const validateForm = () => {
    const newErrors = {};
    const missing   = [];

    if (!formData?.title?.trim()) {
      newErrors.title = 'Presentation title is required';
      missing.push('Presentation Title');
    } else if (formData?.title?.trim()?.length < 3) {
      newErrors.title = 'Title must be at least 3 characters';
      missing.push('Presentation Title (min 3 characters)');
    }
    if (!formData?.industry) {
      newErrors.industry = 'Please select an industry';
      missing.push('Industry');
    }
    if (!formData?.audience) {
      newErrors.audience = 'Please select target audience';
      missing.push('Target Audience');
    }
    if (!formData?.purpose) {
      newErrors.purpose = 'Please select presentation purpose';
      missing.push('Presentation Purpose');
    }
    if (uploadedFiles?.length === 0) {
      newErrors.file = 'Please upload at least one file or provide a link';
      missing.push('Presentation File or Link');
    }

    setErrors(newErrors);
    setTouched({ title: true, industry: true, audience: true, purpose: true, file: true });

    if (missing.length > 0) {
      setValidationAlert(
        `Please complete the following before submitting: ${missing.join(', ')}.`
      );
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return false;
    }

    setValidationAlert('');
    return true;
  };

  // ── Slide count ─────────────────────────────────────────────────────────────
  const resolveSlideCount = async (primaryFile) => {
    if (!primaryFile) return 15;
    if (primaryFile.type === 'link') return 15;
    const ext = primaryFile?.name?.split('.')?.pop()?.toLowerCase();
    if (ext === 'pdf' && primaryFile?.fileObject) return await getPdfPageCount(primaryFile.fileObject);
    if ((ext === 'pptx' || ext === 'ppt') && primaryFile?.size) return getPptxSlideCount(primaryFile.size);
    return 1;
  };

  // ── Submit ──────────────────────────────────────────────────────────────────
  const handleSubmit = async (typeOverride) => {
    if (!validateForm()) return;

    if (!user) {
      setErrors({ submit: 'Please log in to submit presentations' });
      navigate('/login-screen');
      return;
    }

    const resolvedType     = typeOverride || auditType;
    const primaryFile      = uploadedFiles?.[0];
    const actualSlideCount = await resolveSlideCount(primaryFile);

    if (resolvedType === 'paid') {
      navigate(`/paid-audit-checkout-screen?currency=${currency}`, {
        state: {
          submissionData: {
            title:      formData?.title,
            industry:   formData?.industry,
            audience:   formData?.audience,
            purpose:    formData?.purpose,
            notes:      formData?.notes,
            fileName:   primaryFile?.name,
            fileSize:   primaryFile?.size || 0,
            fileType:   primaryFile?.type === 'link'
              ? 'google_slides'
              : primaryFile?.name?.split('.')?.pop()?.toLowerCase(),
            fileUrl:    primaryFile?.type === 'link' ? primaryFile?.url : null,
            fileObject: primaryFile?.type === 'file' ? primaryFile?.fileObject : null,
            slideCount: actualSlideCount,
          },
        },
      });
      return;
    }

    setIsSubmitting(true);
    setErrors({});
    setSuccessMessage('');
    setValidationAlert('');

    try {
      let fileUrl = null, fileSize = primaryFile?.size || 0, fileType = 'google_slides';
      if (primaryFile?.type === 'file' && primaryFile?.fileObject) {
        const uploadResult = await storageService?.uploadDeckFile(
          primaryFile?.fileObject, user?.id
        );
        fileUrl  = uploadResult?.path;
        fileType = primaryFile?.name?.split('.')?.pop()?.toLowerCase();
      } else if (primaryFile?.type === 'link') {
        fileUrl = primaryFile?.url;
      }
      await deckSubmissionService?.createSubmission({
        title:      formData?.title,
        industry:   formData?.industry,
        audience:   formData?.audience,
        purpose:    formData?.purpose,
        notes:      formData?.notes,
        fileName:   primaryFile?.name,
        fileSize,
        fileType,
        fileUrl,
        slideCount: actualSlideCount,
      });
      // Send deck submitted email for free submission
      await emailService.sendDeckSubmittedEmail(
        user?.email,
        user?.user_metadata?.full_name || user?.email?.split('@')[0],
        formData?.title || 'Untitled Deck',
        'free'
      );
      setSuccessMessage('Presentation submitted successfully! Redirecting to dashboard...');
      setTimeout(() => setShowSuccessModal(true), 1500);
    } catch (error) {
      console.error('Submission error:', error);
      setErrors({
        submit: error?.message || 'Failed to submit presentation. Please try again.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleModalClose = () => {
    setShowSuccessModal(false);
    navigate('/user-dashboard');
  };

  const handleSaveDraft = () => {
    console.log('Draft saved:', { formData, uploadedFiles });
    setSuccessMessage('Draft saved successfully!');
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  // ─── Render ─────────────────────────────────────────────────────────────────
  return (
    <div
      className="ifs-root"
      style={{ minHeight: '100vh', background: 'var(--background, #f8fafc)', fontFamily: 'inherit' }}
    >
      {/* Inject page-level styles + CSS variable for gradient */}
      <style>{PAGE_CSS}</style>

      <Helmet>
        <title>Submit Your Presentation - DecX</title>
      </Helmet>

      <RoleBasedHeader userRole={userProfile?.role || 'user'} />
      <AnnouncementBanner />

      <main style={{ fontFamily: 'inherit', paddingTop: 58 }}>
        <div style={{
          maxWidth: 1280,
          margin: '0 auto',
          padding: isMobile ? '20px 16px 40px' : '32px 24px 48px',
        }}>

          {/* ── Page heading ── */}
          <div style={{ marginBottom: isMobile ? 20 : 28 }}>
            <h1 style={{
              fontSize: isMobile ? 24 : 32,
              fontWeight: 700,
              color: '#0f172a',
              margin: '0 0 6px',
              letterSpacing: '-0.02em',
            }}>
              Submit Your Presentation
            </h1>
            <p style={{
              fontFamily: 'inherit',
              fontSize: isMobile ? 13 : 15,
              color: '#64748b',
              margin: 0,
              lineHeight: 1.6,
            }}>
              Upload your deck and provide context for comprehensive DecX Score analysis
            </p>
          </div>

          {/* ── Validation alert ── */}
          {validationAlert && (
            <div style={{
              marginBottom: 16,
              background: '#fff7ed',
              border: '1px solid #fed7aa',
              borderRadius: 10,
              padding: isMobile ? '12px 14px' : '14px 16px',
              display: 'flex', alignItems: 'flex-start', gap: 10,
            }}>
              <Icon name="AlertTriangle" size={18} color="#ea580c" style={{ marginTop: 1, flexShrink: 0 }} />
              <div>
                <p style={{
                  fontFamily: 'inherit', fontSize: 13,
                  color: '#c2410c', margin: '0 0 3px', fontWeight: 600,
                }}>
                  Please complete all required fields
                </p>
                <p style={{ fontFamily: 'inherit', fontSize: 12, color: '#9a3412', margin: 0 }}>
                  {validationAlert}
                </p>
              </div>
            </div>
          )}

          {/* ── Success banner ── */}
          {successMessage && (
            <div style={{
              marginBottom: 16, background: '#f0fdf4',
              border: '1px solid #bbf7d0', borderRadius: 10,
              padding: isMobile ? '12px 14px' : '14px 16px',
              display: 'flex', alignItems: 'flex-start', gap: 10,
            }}>
              <Icon name="CheckCircle2" size={18} color="#16a34a" style={{ marginTop: 1, flexShrink: 0 }} />
              <p style={{ fontFamily: 'inherit', fontSize: 13, color: '#15803d', margin: 0, fontWeight: 500 }}>
                {successMessage}
              </p>
            </div>
          )}

          {/* ── Submit error ── */}
          {errors?.submit && (
            <div style={{
              marginBottom: 16, background: '#fef2f2',
              border: '1px solid #fecaca', borderRadius: 10,
              padding: isMobile ? '12px 14px' : '14px 16px',
              display: 'flex', alignItems: 'flex-start', gap: 10,
            }}>
              <Icon name="AlertCircle" size={18} color="#dc2626" style={{ marginTop: 1, flexShrink: 0 }} />
              <p style={{ fontFamily: 'inherit', fontSize: 13, color: '#dc2626', margin: 0 }}>
                {errors?.submit}
              </p>
            </div>
          )}

          {/* ── Two-column layout (stacks on mobile) ── */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
            gap: isMobile ? 20 : 28,
          }}>

            {/* Left — Upload */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? 16 : 20 }}>
              <UnifiedUploadSection
                onFileSelect={handleFileSelect}
                onLinkSubmit={handleLinkSubmit}
                acceptedFormats={acceptedFormats}
                maxSize={maxFileSize}
              />

              {errors?.file && touched?.file && uploadedFiles?.length === 0 && (
                <div style={{
                  background: '#fff7ed', border: '1px solid #fed7aa',
                  borderRadius: 10, padding: '12px 14px',
                  display: 'flex', alignItems: 'center', gap: 8,
                }}>
                  <Icon name="AlertTriangle" size={16} color="#ea580c" style={{ flexShrink: 0 }} />
                  <p style={{ fontFamily: 'inherit', fontSize: 12, color: '#c2410c', margin: 0 }}>
                    {errors?.file}
                  </p>
                </div>
              )}

              {uploadedFiles?.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <h3 style={{
                      fontSize: 15, fontWeight: 700,
                      color: '#0f172a', margin: 0,
                    }}>
                      Uploaded Files ({uploadedFiles?.length})
                    </h3>
                    <Icon name="CheckCircle2" size={16} color="#16a34a" />
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {uploadedFiles?.map((file) => (
                      <FilePreviewCard
                        key={file?.id}
                        file={file}
                        onRemove={() => handleRemoveFile(file?.id)}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Right — Context Form */}
            <div>
              <PresentationContextForm
                formData={formData}
                onChange={handleFormDataChange}
                errors={errors}
                touched={touched}
                onFieldBlur={handleFieldBlur}
              />
            </div>
          </div>

          {/* ── Audit Type Selector ── */}
          <div style={{ marginTop: isMobile ? 20 : 28 }}>
            <AuditTypeSelector
              selectedType={auditType}
              onTypeChange={setAuditType}
              onSubmit={(type) => {
                setAuditType(type);
                handleSubmit(type);
              }}
              isSubmitting={isSubmitting}
              currency={currency}
              onCurrencyChange={setCurrency}
            />
          </div>

          {/* ── Save Draft ── */}
          <div style={{
            display: 'flex',
            justifyContent: isMobile ? 'stretch' : 'flex-end',
            marginTop: 14,
          }}>
            <Button
              variant="outline"
              size="lg"
              iconName="Save"
              iconPosition="left"
              onClick={handleSaveDraft}
              disabled={isSubmitting}
              style={isMobile ? { width: '100%' } : {}}
            >
              Save Draft
            </Button>
          </div>

        </div>
      </main>

      <SubmissionSuccessModal
        isOpen={showSuccessModal}
        onClose={handleModalClose}
        userEmail={user?.email}
      />
    </div>
  );
};

export default InitialFormUploadScreen;